import React, { useState, useEffect } from 'react';
import Row from 'containers/Reports/row';
import { connect } from 'react-redux';
import { runPrescreenReport } from 'actions/reports';
import Select from 'react-select';
import { FormGroup, Switch } from '@blueprintjs/core';
import {
  fetchActivePrescreenQuestions,
  fetchArchivedPrescreenQuestions,
} from 'actions/prescreen';
import { fetchAllRequestStatus, fetchQuestionStatus } from 'actions/utils';
import { DatePicker } from '@blueprintjs/datetime';
import moment from 'moment';
import VERSION_STATUS from 'utils/constants';
import { getMaxDate } from 'utils/functions';

const PrescreenQuestionsBase = ({
  fetchActivePrescreenQuestions,
  fetchArchivedPrescreenQuestions,
  fetchAllRequestStatus,
  fetchQuestionStatus,
  runPrescreenReport,
  activeQuestions,
  archivedQuestions,
  activeLoaded,
  archivedLoaded,
  requestStatus,
  versionStatus,
  versionStatusLoaded,
  isLoadedStatus,
  report,
  ...rest
}) => {
  const [activeOrArchived, setActiveOrArchived] = useState(true);
  const [selectedQuestion, setQuestion] = useState(null);
  const [selectedAnswer, setAnswer] = useState([]);
  const [selectedStatus, setStatus] = useState(null);
  const [selectedRenewalDate, setRenewalDate] = useState(null);
  const [selectedApprovalDate, setApprovalDate] = useState(null);

  const getStatusID = statusName =>
    versionStatus.find(status => status.statusName === statusName)
      .versionStatusID;

  useEffect(() => {
    if (versionStatusLoaded) {
      if (activeOrArchived && !activeLoaded) {
        fetchActivePrescreenQuestions({
          status: getStatusID(VERSION_STATUS.ACTIVE_VERSION_STATUS),
          start: 0,
          limit: -1,
        });
      } else if (!activeOrArchived && !archivedLoaded) {
        fetchArchivedPrescreenQuestions({
          status: getStatusID(VERSION_STATUS.ARCHIVED_VERSION_STATUS),
          start: 0,
          limit: -1,
        });
      }
    }
  }, [activeOrArchived, versionStatusLoaded]);

  const handleChange = (selected, setter) => {
    setter(selected);
  };

  const handleQuestionChange = selected => {
    setQuestion(selected);
    setAnswer([]);
  };

  const handleAnswerChange = selected => {
    setAnswer(selected);
  };

  const toggleActiveOrArchived = () => {
    if (activeOrArchived) {
      setActiveOrArchived(false);
    } else {
      setActiveOrArchived(true);
    }
    setAnswer([]);
    setQuestion(null);
  };

  return (
    <Row
      name="Pre-Screen Questions"
      description="Assessments by the selected answer(s) to a specific pre-screen question."
      drawer={
        <div style={{ width: '75%' }}>
          <Switch
            checked={activeOrArchived}
            onChange={toggleActiveOrArchived}
            label="Active or Archived Questions"
            innerLabel="Archived"
            innerLabelChecked="Active"
            large
          />
          <FormGroup label="Question" labelInfo="(required)">
            <Select
              options={activeOrArchived ? activeQuestions : archivedQuestions}
              getOptionLabel={option => option.question}
              getOptionValue={option => option.questionID}
              value={selectedQuestion}
              onChange={handleQuestionChange}
            />
          </FormGroup>
          <FormGroup label="Answer" labelInfo="(required)">
            <Select
              options={selectedQuestion ? selectedQuestion.answers : []}
              getOptionLabel={option => option.answer}
              getOptionValue={option => option.answerID}
              value={selectedAnswer}
              onChange={handleAnswerChange}
              isMulti
            />
          </FormGroup>
          <FormGroup label="Assessment Status">
            <Select
              options={requestStatus}
              getOptionLabel={option => option.statusName}
              getOptionValue={option => option.statusName}
              value={selectedStatus}
              onChange={selected => handleChange(selected, setStatus)}
            />
          </FormGroup>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <FormGroup label="Approval Date On or After">
              <DatePicker
                value={selectedApprovalDate}
                onChange={selected => handleChange(selected, setApprovalDate)}
                maxDate={getMaxDate()}
              />
            </FormGroup>
            <FormGroup label="Renewal Date On or After">
              <DatePicker
                value={selectedRenewalDate}
                onChange={selected => handleChange(selected, setRenewalDate)}
                maxDate={getMaxDate()}
              />
            </FormGroup>
          </div>
        </div>
      }
      runReport={() =>
        runPrescreenReport({
          prescreenQuestion: selectedQuestion.questionID,
          prescreenAnswer: JSON.stringify(
            selectedAnswer.map(answer => answer.answerID)
          ),
          requestStatus: selectedStatus && selectedStatus.statusName,
          ...(moment(selectedRenewalDate).isValid() && {
            renewalDate: {
              date: moment(selectedRenewalDate).format('YYYY-MM-DD'),
              direction: 'AFTER',
            },
          }),
          ...(moment(selectedApprovalDate).isValid() && {
            approvalDate: {
              date: moment(selectedApprovalDate).format('YYYY-MM-DD'),
              direction: 'AFTER',
            },
          }),
        })
      }
      data={report}
      disableRunReport={!!!selectedQuestion || !!!selectedAnswer.length}
      headers={[
        { label: 'Assessment ID', key: 'vendorRequestID' },
        {
          label: 'Third Party Name',
          key: 'vendorName',
        },
        { label: 'Product / Service', key: 'productServiceName' },
        { label: 'Status', key: 'requestStatus' },
        { label: 'Approval Date', key: 'approvalDate' },
        { label: 'Renewal Date', key: 'renewalDate' },
        { label: 'Sponsor Name', key: 'sponsorName' },
        { label: 'Department', key: 'sponsorDepartment' },
        { label: 'Requesting Department', key: 'departmentsRequesting' },
        { label: 'Question Types', key: 'questionTypes' },
      ]}
      resetDrawerState={() => {
        setActiveOrArchived(true);
        setQuestion(null);
        setAnswer([]);
        setStatus(null);
        setRenewalDate(null);
        setApprovalDate(null);
      }}
      fetchReportFieldsData={() => {
        if (!isLoadedStatus) {
          fetchAllRequestStatus();
        }
        if (!versionStatusLoaded) {
          fetchQuestionStatus();
        }
      }}
      {...rest}
    />
  );
};

const mapStateToProps = state => ({
  report: state.reports.prescreen,
  isFetchingReport: state.reports.isFetching,
  isLoadedReport: state.reports.isLoaded,
  errorMessage: state.reports.errorMessage,
  activeQuestions: state.prescreen.activeQuestions,
  archivedQuestions: state.prescreen.archivedQuestions,
  activeLoaded: state.prescreen.activeLoaded,
  archivedLoaded: state.prescreen.archivedLoaded,
  requestStatus: state.utils.requestStatus,
  versionStatus: state.utils.questionStatus,
  versionStatusLoaded: state.utils.questionStatusLoaded,
  isLoadedStatus: state.utils.isLoadedRequestStatus,
});

const PrescreenQuestions = connect(
  mapStateToProps,
  {
    fetchActivePrescreenQuestions,
    fetchArchivedPrescreenQuestions,
    fetchAllRequestStatus,
    runPrescreenReport,
    fetchQuestionStatus,
  }
)(PrescreenQuestionsBase);

export default PrescreenQuestions;
